import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  XAxis,
  YAxis,
} from "@repo/ui/chart";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { cn } from "@repo/ui/lib/utils";
import {
  AlertCircle,
  BarChart3,
  CheckCircle,
  Code,
  Globe,
  Loader2,
  Plus,
  RefreshCw,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import type { AdapterRecord as Adapter, AdapterStatsSnapshotRecord } from "@/lib/api";
import {
  fetchAdapterStatsRuns,
  fetchAdapters,
  fetchLatestAdapterStatsSnapshots,
  runAdapterStatsMaterialization,
} from "@/lib/api";
import { formatDate } from "@/lib/formatDate";

type AdapterStatus = "active" | "development" | "disabled";

const statusConfig: Record<
  AdapterStatus,
  { icon: typeof CheckCircle; label: string; className: string }
> = {
  active: {
    icon: CheckCircle,
    label: "Active",
    className: "bg-success/15 text-success border-success/30",
  },
  development: {
    icon: Code,
    label: "In Development",
    className: "bg-warning/15 text-warning border-warning/30",
  },
  disabled: {
    icon: AlertCircle,
    label: "Disabled",
    className: "bg-muted text-muted-foreground border-muted",
  },
};

const Adapters = () => {
  const { toast } = useToast();
  const [adapters, setAdapters] = useState<Adapter[]>([]);
  const [stats, setStats] = useState<AdapterStatsSnapshotRecord[]>([]);
  const [statsRuns, setStatsRuns] = useState<
    Array<{
      id: string;
      trigger_type: string;
      status: string;
      benchmark_adapters: string[];
      scope_filters: Record<string, unknown>;
      started_at: string;
      completed_at: string | null;
      error_message: string | null;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingStats, setIsRefreshingStats] = useState(false);
  const [statsScopeState, setStatsScopeState] = useState("");
  const [statsScopeSuburb, setStatsScopeSuburb] = useState("");
  const [statsScopeFromDate, setStatsScopeFromDate] = useState("");
  const [statsScopeToDate, setStatsScopeToDate] = useState("");
  const [benchmarkAdaptersText, setBenchmarkAdaptersText] = useState(
    "domain-com-au, realestate-com-au",
  );

  const loadAdapters = useCallback(async () => {
    try {
      setIsLoading(true);
      const [adapterData, statData, runsData] = await Promise.all([
        fetchAdapters(),
        fetchLatestAdapterStatsSnapshots(),
        fetchAdapterStatsRuns(5),
      ]);
      setAdapters(adapterData);
      setStats(statData);
      setStatsRuns(runsData);
    } catch (error) {
      console.error("Failed to load adapters:", error);
      toast({
        title: "Error",
        description: "Failed to load adapters",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAdapters();
  }, [loadAdapters]);

  const statsBySlug = useMemo(
    () => new Map(stats.map((item) => [item.adapter_slug, item])),
    [stats],
  );

  const overviewChartData = useMemo(() => {
    return adapters
      .map((adapter) => {
        const stat = statsBySlug.get(adapter.slug);
        return {
          adapter: adapter.slug,
          unique: stat?.unique_properties || 0,
          overlap: stat?.overlap_properties || 0,
          benchmarkOnly: stat?.benchmark_unique_properties || 0,
        };
      })
      .sort(
        (a, b) => b.unique + b.overlap + b.benchmarkOnly - (a.unique + a.overlap + a.benchmarkOnly),
      )
      .slice(0, 8);
  }, [adapters, statsBySlug]);

  const handleRefreshStats = async () => {
    try {
      setIsRefreshingStats(true);
      const benchmarkAdapters = benchmarkAdaptersText
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
      const scopeFilters = {
        state: statsScopeState || undefined,
        suburb: statsScopeSuburb || undefined,
        fromDate: statsScopeFromDate || undefined,
        toDate: statsScopeToDate || undefined,
      };
      const result = await runAdapterStatsMaterialization({
        triggerType: "manual",
        benchmarkAdapters: benchmarkAdapters.length > 0 ? benchmarkAdapters : undefined,
        scopeFilters,
      });
      if (!result.success) {
        throw new Error(result.error || "Stats refresh failed");
      }
      toast({
        title: "Adapter stats refreshed",
        description: "New overlap and uniqueness metrics have been materialized.",
      });
      await loadAdapters();
    } catch (error) {
      console.error("Failed to refresh adapter stats:", error);
      toast({
        title: "Stats refresh failed",
        description: "Could not compute adapter intelligence stats",
        variant: "destructive",
      });
    } finally {
      setIsRefreshingStats(false);
    }
  };

  const chartConfig = {
    unique: { label: "Unique to adapter", color: "hsl(var(--chart-1))" },
    overlap: { label: "Overlap with big guys", color: "hsl(var(--chart-2))" },
    benchmarkOnly: { label: "Unique to big guys", color: "hsl(var(--chart-3))" },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Adapters</h1>
          <p className="text-muted-foreground">
            Website adapters for data extraction. Each adapter handles scraping for a specific
            website.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefreshStats} disabled={isRefreshingStats}>
            {isRefreshingStats ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh Stats
          </Button>
          <Button disabled>
            <Plus className="w-4 h-4 mr-2" />
            Add Adapter
          </Button>
        </div>
      </div>

      <div className="card-elevated p-4 space-y-3">
        <div>
          <h2 className="text-sm font-semibold">Stats Run Scope (Manual)</h2>
          <p className="text-xs text-muted-foreground">
            Optional filters for business-intelligence snapshots (state/suburb/date range +
            benchmark set).
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="space-y-1">
            <Label htmlFor="stats-scope-state" className="text-xs">
              State
            </Label>
            <Input
              id="stats-scope-state"
              placeholder="e.g. VIC"
              value={statsScopeState}
              onChange={(event) => setStatsScopeState(event.target.value.toUpperCase())}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="stats-scope-suburb" className="text-xs">
              Suburb
            </Label>
            <Input
              id="stats-scope-suburb"
              placeholder="e.g. Richmond"
              value={statsScopeSuburb}
              onChange={(event) => setStatsScopeSuburb(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="stats-scope-from-date" className="text-xs">
              From Date
            </Label>
            <Input
              id="stats-scope-from-date"
              type="date"
              value={statsScopeFromDate}
              onChange={(event) => setStatsScopeFromDate(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="stats-scope-to-date" className="text-xs">
              To Date
            </Label>
            <Input
              id="stats-scope-to-date"
              type="date"
              value={statsScopeToDate}
              onChange={(event) => setStatsScopeToDate(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="benchmark-adapters" className="text-xs">
              Benchmark Adapters
            </Label>
            <Input
              id="benchmark-adapters"
              value={benchmarkAdaptersText}
              onChange={(event) => setBenchmarkAdaptersText(event.target.value)}
              placeholder="domain-com-au, realestate-com-au"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {adapters.map((adapter) => {
            const status =
              statusConfig[adapter.status as AdapterStatus] || statusConfig.development;
            const StatusIcon = status.icon;
            const defaultConfig = (adapter.default_config || {}) as { propertyTypes?: string[] };
            const stat = statsBySlug.get(adapter.slug);
            const totalVenn =
              (stat?.unique_properties || 0) +
              (stat?.overlap_properties || 0) +
              (stat?.benchmark_unique_properties || 0);
            const uniquePct =
              totalVenn > 0 ? ((stat?.unique_properties || 0) / totalVenn) * 100 : 0;
            const overlapPct =
              totalVenn > 0 ? ((stat?.overlap_properties || 0) / totalVenn) * 100 : 0;
            const benchmarkPct =
              totalVenn > 0 ? ((stat?.benchmark_unique_properties || 0) / totalVenn) * 100 : 0;

            return (
              <div key={adapter.id} className="card-elevated p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{adapter.name}</h3>
                      <a
                        href={adapter.base_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted-foreground hover:text-accent transition-colors"
                      >
                        {adapter.base_url.replace("https://", "").replace("www.", "")}
                      </a>
                    </div>
                  </div>
                  <Badge className={cn("border", status.className)}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {status.label}
                  </Badge>
                </div>

                <p className="text-sm text-muted-foreground line-clamp-2">
                  {adapter.description || "No description available"}
                </p>

                {/* Show supported property types */}
                {defaultConfig.propertyTypes && (
                  <div className="flex flex-wrap gap-1">
                    {defaultConfig.propertyTypes.slice(0, 4).map((type: string) => (
                      <Badge key={type} variant="outline" className="text-xs">
                        {type}
                      </Badge>
                    ))}
                    {defaultConfig.propertyTypes.length > 4 && (
                      <Badge variant="outline" className="text-xs">
                        +{defaultConfig.propertyTypes.length - 4}
                      </Badge>
                    )}
                  </div>
                )}

                <div className="space-y-2 border rounded-md p-3 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground uppercase">Intelligence</span>
                    {stat ? (
                      <Badge variant="secondary" className="text-xs">
                        {formatDate(stat.captured_at, { includeTime: true })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        No stats yet
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-muted-foreground">Properties</p>
                      <p className="font-semibold">
                        {(stat?.total_properties || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Snapshots</p>
                      <p className="font-semibold">
                        {(stat?.total_snapshots || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Fresh 7d</p>
                      <p className="font-semibold">
                        {(stat?.fresh_7d_count || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="h-2 rounded-full overflow-hidden flex">
                      <div style={{ width: `${uniquePct}%` }} className="bg-chart-1" />
                      <div style={{ width: `${overlapPct}%` }} className="bg-chart-2" />
                      <div style={{ width: `${benchmarkPct}%` }} className="bg-chart-3" />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>Unique {(stat?.unique_properties || 0).toLocaleString()}</span>
                      <span>Overlap {(stat?.overlap_properties || 0).toLocaleString()}</span>
                      <span>
                        Big-only {(stat?.benchmark_unique_properties || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-[10px] text-muted-foreground">
                    <span>
                      Fail {stat?.fail_rate ? `${Math.round(stat.fail_rate * 100)}%` : "—"}
                    </span>
                    <span>
                      Block {stat?.block_rate ? `${Math.round(stat.block_rate * 100)}%` : "—"}
                    </span>
                    <span>
                      p50 {stat?.median_duration_ms ? `${stat.median_duration_ms}ms` : "—"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-border text-xs text-muted-foreground gap-2">
                  <span>Added {formatDate(adapter.created_at)}</span>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <Link to={`/dashboard/adapters/${adapter.slug}`}>Visibility</Link>
                    </Button>
                    <Badge variant="secondary" className="text-xs">
                      {adapter.slug}
                    </Badge>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card-elevated p-5 space-y-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold">Adapter Overlap Overview</h3>
        </div>
        {overviewChartData.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Run a stats refresh to populate overlap analytics.
          </p>
        ) : (
          <ChartContainer config={chartConfig} className="h-[280px] w-full">
            <BarChart data={overviewChartData}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="adapter" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis allowDecimals={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="unique" stackId="a" fill="var(--color-unique)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="overlap" stackId="a" fill="var(--color-overlap)" />
              <Bar
                dataKey="benchmarkOnly"
                stackId="a"
                fill="var(--color-benchmarkOnly)"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ChartContainer>
        )}
      </div>

      {statsRuns.length > 0 && (
        <div className="card-elevated p-5 space-y-2">
          <h3 className="font-semibold text-sm">Recent Stats Runs</h3>
          {statsRuns.map((run) => (
            <div
              key={run.id}
              className="flex items-center justify-between text-xs border rounded p-2"
            >
              <span>
                {run.trigger_type} • {run.status} •{" "}
                {formatDate(run.started_at, { includeTime: true })}
              </span>
              <span className="text-muted-foreground">{run.benchmark_adapters.join(", ")}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card-elevated p-6 border-dashed">
        <div className="text-center space-y-2">
          <Code className="w-8 h-8 mx-auto text-muted-foreground" />
          <h3 className="font-medium">Adding New Adapters</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Adapters are added by developers. Each adapter is a module that defines how to connect
            to and scrape data from a specific website using configurable providers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Adapters;
